import { useState, useMemo, useEffect, useCallback } from "react";
import {
  Button,
  Card,
  Flex,
  Tag,
  Text,
  LoadingIndicator,
  TypeaheadInput,
  Select,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import { useAppContext } from "~/contexts/appContext";
import { DataStudioJobSubstateEnum, DataStudioJobTypeEnum } from "~/graphql/supergraphEnums";
import { type DataStudioJobEnvironmentEnum } from "~/graphql/supergraphTypes";
import { MAX_JOBS_LIMIT, EffectiveStatus } from "~/hooks/useDataStudioQueries/constants";
import {
  useListOnboardingJobs,
  useGetJobMetadata,
} from "~/hooks/useDataStudioQueries/useDataStudioQueries";
import { canSeeMyReviewsTab } from "~/utils/userRoles";
import { JobLogsModal } from "./components/JobLogsModal";
import * as styles from "./styles_vanilla.css";
import {
  ENVIRONMENT_OPTIONS,
  ACTIVE_STATUSES,
  COMPLETED_STATUSES,
  FAILED_STATUSES,
  BACKEND_ACTIVE_STATUSES,
  BACKEND_COMPLETED_STATUSES,
  BACKEND_FAILED_STATUSES,
} from "./utils/constants";
import { formatJobType, buildCrmImportUrl } from "./utils/jobHelpers";
import { getJobTypeOptionsForUser } from "./utils/jobTypeOptions";
import {
  getEffectiveStatus,
  getStatusLabel,
  getStatusTagClassName,
  getProgressFillClassName,
} from "./utils/statusHelpers";
import type { OnboardingJob } from "~/hooks/useDataStudioQueries/types";

const isValidJobType = (value: string): value is DataStudioJobTypeEnum => {
  return Object.values(DataStudioJobTypeEnum).some((enumValue) => enumValue === value);
};

const isValidEnvironment = (value: string): value is DataStudioJobEnvironmentEnum => {
  return value === "TEST" || value === "PRODUCTION";
};

export const Dashboard = () => {
  const { user } = useAppContext();

  const [searchTerm, setSearchTerm] = useState("");
  const [jobTypeFilter, setJobTypeFilter] = useState("all");
  const [environmentFilter, setEnvironmentFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("all");
  const [selectedJob, setSelectedJob] = useState<OnboardingJob | null>(null);
  const [isJobLogsOpen, setIsJobLogsOpen] = useState(false);

  // Build filters object
  const jobTypeOptions = useMemo(() => getJobTypeOptionsForUser(user.roles), [user.roles]);

  const filters = useMemo(() => {
    const result: Record<string, string> = {};
    if (jobTypeFilter !== "all" && isValidJobType(jobTypeFilter)) {
      result.jobType = jobTypeFilter;
    }
    if (environmentFilter !== "all" && isValidEnvironment(environmentFilter)) {
      result.environment = environmentFilter;
    }
    return Object.keys(result).length > 0 ? result : undefined;
  }, [jobTypeFilter, environmentFilter]);

  const {
    data: onboardingJobsData,
    loading: jobsLoading,
    error: jobsError,
    refetch,
  } = useListOnboardingJobs({
    limit: MAX_JOBS_LIMIT,
    filters,
  });

  const {
    data: metadataData,
    loading: metadataLoading,
    error: metadataError,
    refetch: refetchMetadata,
  } = useGetJobMetadata();

  const jobs = useMemo(
    () => onboardingJobsData?.listOnboardingJobs?.jobs ?? [],
    [onboardingJobsData?.listOnboardingJobs?.jobs],
  );

  const getFilteredJobCount = useCallback(
    (statusFilter?: EffectiveStatus[]) => {
      return jobs.filter((job) => {
        const matchesSearch =
          job.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.onboardingManager.name?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus =
          !statusFilter ||
          statusFilter.includes(getEffectiveStatus(job.jobType, job.jobStatus));

        return matchesSearch && matchesStatus;
      }).length;
    },
    [jobs, searchTerm],
  );

  const tabCounts = useMemo(
    () => ({
      all: getFilteredJobCount(),
      active: getFilteredJobCount(ACTIVE_STATUSES),
      completed: getFilteredJobCount(COMPLETED_STATUSES),
      failed: getFilteredJobCount(FAILED_STATUSES),
    }),
    [getFilteredJobCount],
  );

  const filteredJobs = useMemo(
    () =>
      jobs
        .filter((job) => {
          const { jobType, jobStatus, customerName, onboardingManager } = job;
          const effectiveStatus = getEffectiveStatus(jobType, jobStatus);
          const matchesSearch =
            customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            onboardingManager.name?.toLowerCase().includes(searchTerm.toLowerCase());
          const matchesTab =
            activeTab === "all" ||
            (activeTab === "active" && ACTIVE_STATUSES.includes(effectiveStatus)) ||
            (activeTab === "completed" && COMPLETED_STATUSES.includes(effectiveStatus)) ||
            (activeTab === "failed" && FAILED_STATUSES.includes(effectiveStatus));

          return matchesSearch && matchesTab;
        })
        .sort((a, b) => {
          const dateA = new Date(a.createdAt);
          const dateB = new Date(b.createdAt);
          return dateB.getTime() - dateA.getTime();
        }),
    [jobs, searchTerm, activeTab],
  );

  // Clear search when job type or environment filter changes
  useEffect(() => {
    setSearchTerm("");
  }, [jobTypeFilter, environmentFilter]);

  const getJobStats = () => {
    const metadataHasErrors =
      metadataError ||
      !metadataData?.dataStudioGetJobMetadata?.success ||
      (metadataData?.dataStudioGetJobMetadata?.errors?.length ?? 0) > 0;

    if (metadataData?.dataStudioGetJobMetadata?.success && !metadataHasErrors) {
      const metadata = metadataData.dataStudioGetJobMetadata;

      const inProgressCount = metadata.byStatus
        .filter((s) => BACKEND_ACTIVE_STATUSES.includes(s.status))
        .reduce((sum, s) => sum + s.count, 0);

      const completedCount = metadata.byStatus
        .filter((s) => BACKEND_COMPLETED_STATUSES.includes(s.status))
        .reduce((sum, s) => sum + s.count, 0);

      const failedCount = metadata.byStatus
        .filter((s) => BACKEND_FAILED_STATUSES.includes(s.status))
        .reduce((sum, s) => sum + s.count, 0);

      const stats = {
        total: metadata.totalJobs,
        active: inProgressCount,
        completed: completedCount,
        failed: failedCount,
      };
      return stats;
    }

    const totalCount = onboardingJobsData?.listOnboardingJobs?.totalCount || jobs.length;

    const stats = {
      total: totalCount,
      active: jobs.filter((j) =>
        ACTIVE_STATUSES.includes(getEffectiveStatus(j.jobType, j.jobStatus)),
      ).length,
      completed: jobs.filter((j) =>
        COMPLETED_STATUSES.includes(getEffectiveStatus(j.jobType, j.jobStatus)),
      ).length,
      failed: jobs.filter((j) =>
        FAILED_STATUSES.includes(getEffectiveStatus(j.jobType, j.jobStatus)),
      ).length,
    };
    return stats;
  };

  const stats = getJobStats();

  const refreshJobs = () => {
    // Reset all filters to default values
    setSearchTerm("");
    setJobTypeFilter("all");
    setEnvironmentFilter("all");
    setActiveTab("all");

    // Refetch data
    void refetch();
    void refetchMetadata();
  };

  const handleDetailsClick = (job: OnboardingJob) => {
    const isCrmImport = job.jobType === DataStudioJobTypeEnum.CrmImport;
    const isTestDataRelease = job.substate === DataStudioJobSubstateEnum.ScheduleImport;

    if (isCrmImport && !isTestDataRelease) {
      const url = buildCrmImportUrl(job.jobType, job.substate, job.jobId, job.createdAt);
      window.location.href = url;
    } else {
      setSelectedJob(job);
      setIsJobLogsOpen(true);
    }
  };

  if (jobsLoading) {
    return (
      <div className={styles.containerStyle}>
        <div className={styles.headerStyle}>
          <Flex justifyContent="center" alignItems="center" className={styles.loadingContainer}>
            <LoadingIndicator variant="primary" />
          </Flex>
        </div>
      </div>
    );
  }

  if (jobsError) {
    return (
      <div className={styles.containerStyle}>
        <div className={styles.headerStyle}>
          <Flex
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            className={styles.errorContainer}
          >
            <Icon type="exclamation-triangle" size="large" color={Colors.Danger600} />
            <Text variant="h3" className={styles.errorTitle}>
              Error Loading Jobs
            </Text>
            <Text variant="secondary-md" className={styles.errorMaxWidth}>
              There was an error loading the onboarding jobs. This might be because the backend
              resolvers are not yet implemented.
            </Text>
            <Button onClick={refreshJobs} variant="primary">
              Try Again
            </Button>
          </Flex>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.containerStyle} data-testid="data-studio-dashboard">
      {/* Header */}
      <div className={styles.headerStyle}>
        <Flex justifyContent="space-between" alignItems="center">
          <Flex alignItems="center" className={styles.headerGap}>
            <Icon
              type="chart-bar"
              size="medium"
              color={Colors.Neutral0}
              className={styles.headerIcon}
            />
            <div>
              <Text variant="h2" className={styles.headerTitle}>
                Dashboard
              </Text>
              <Text variant="secondary-md">Monitor and manage import jobs</Text>
            </div>
          </Flex>
          <Flex gap="md">
            <Button onClick={refreshJobs} variant="neutral" icon="sync-alt">
              Refresh
            </Button>
          </Flex>
        </Flex>
      </div>

      {/* Metadata Error Warning */}
      {(metadataError ||
        (metadataData?.dataStudioGetJobMetadata?.errors &&
          metadataData.dataStudioGetJobMetadata.errors.length > 0)) && (
        <Card className={`inset-md ${styles.metadataWarningCard}`}>
          <Flex alignItems="center" gap="md">
            <Icon type="exclamation-triangle" size="medium" color={Colors.Warning700} />
            <div className={styles.warningCardContent}>
              <Text variant="strong-md" className={styles.warningTitle}>
                Stats Calculation Issue
              </Text>
              <Text variant="secondary-sm" className={styles.warningText}>
                Unable to fetch job metadata from backend. Showing stats calculated from visible
                jobs only.
              </Text>
              {metadataData?.dataStudioGetJobMetadata?.errors && (
                <Text variant="secondary-sm" className={styles.warningErrorText}>
                  Error: {metadataData.dataStudioGetJobMetadata.errors.join(", ")}
                </Text>
              )}
            </div>
          </Flex>
        </Card>
      )}

      {/* Stats Cards */}
      <div className={styles.statsGridStyle}>
        <Card className="inset-md">
          <Flex justifyContent="space-between" alignItems="center">
            <div>
              <Text variant="secondary-sm">Total Jobs</Text>
              {metadataLoading ? (
                <LoadingIndicator variant="primary" />
              ) : (
                <Text variant="strong-xl">{stats.total}</Text>
              )}
            </div>
            <Icon type="chart-bar" size="large" color={Colors.Neutral600} />
          </Flex>
        </Card>

        <Card className="inset-md">
          <Flex justifyContent="space-between" alignItems="center">
            <div>
              <Text variant="secondary-sm">Active</Text>
              {metadataLoading ? (
                <LoadingIndicator variant="primary" />
              ) : (
                <Text variant="strong-xl" className={styles.statsActiveText}>
                  {stats.active}
                </Text>
              )}
            </div>
            <Icon type="sync-alt" size="large" color={Colors.Primary600} />
          </Flex>
        </Card>

        <Card className="inset-md">
          <Flex justifyContent="space-between" alignItems="center">
            <div>
              <Text variant="secondary-sm">Completed</Text>
              {metadataLoading ? (
                <LoadingIndicator variant="primary" />
              ) : (
                <Text variant="strong-xl" className={styles.statsCompletedText}>
                  {stats.completed}
                </Text>
              )}
            </div>
            <Icon type="check" size="large" color={Colors.Success600} />
          </Flex>
        </Card>

        <Card className="inset-md">
          <Flex justifyContent="space-between" alignItems="center">
            <div>
              <Text variant="secondary-sm">Failed</Text>
              {metadataLoading ? (
                <LoadingIndicator variant="primary" />
              ) : (
                <Text variant="strong-xl" className={styles.statsFailedText}>
                  {stats.failed}
                </Text>
              )}
            </div>
            <Icon type="exclamation-circle" size="large" color={Colors.Danger600} />
          </Flex>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card className={`inset-md ${styles.searchFiltersCard}`}>
        <Flex gap={Sizes.MD} alignItems="center" flexWrap="wrap">
          <div className={styles.searchContainer}>
            <TypeaheadInput
              key={`${jobTypeFilter}-${environmentFilter}`}
              placeholder="Search jobs by Arena Domain or PO User"
              options={[]}
              isClearable
              isFullWidth
              onDebouncedSearch={(term: string) => setSearchTerm(term)}
              onClearValue={() => setSearchTerm("")}
              shouldClearOnSelect={false}
            />
          </div>
          <Select
            value={jobTypeFilter}
            onChange={(val) => setJobTypeFilter(String(val))}
            placeholder="All Job Types"
          >
            <Select.Section>
              <Select.Option value="all">All Job Types</Select.Option>
            </Select.Section>
            <Select.Section>
              {jobTypeOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select.Section>
          </Select>
          <Select
            value={environmentFilter}
            onChange={(val) => setEnvironmentFilter(String(val))}
            placeholder="All Environments"
          >
            <Select.Section>
              <Select.Option value="all">All Environments</Select.Option>
            </Select.Section>
            <Select.Section>
              {ENVIRONMENT_OPTIONS.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select.Section>
          </Select>
        </Flex>
      </Card>

      {/* Import Jobs Section */}
      <Card className={`inset-lg ${styles.importJobsCard}`}>
        <Text variant="strong-lg" className={styles.importJobsTitle}>
          Import Jobs
        </Text>

        <div className={styles.tabsContainerStyle}>
          <Button
            variant="neutral"
            className={styles.customTabStyle}
            data-active={activeTab === "all"}
            onClick={() => setActiveTab("all")}
          >
            {`All (${tabCounts.all})`}
          </Button>
          <Button
            variant="neutral"
            className={styles.customTabStyle}
            data-active={activeTab === "active"}
            onClick={() => setActiveTab("active")}
          >
            {`Active (${tabCounts.active})`}
          </Button>
          <Button
            variant="neutral"
            className={styles.customTabStyle}
            data-active={activeTab === "completed"}
            onClick={() => setActiveTab("completed")}
          >
            {`Completed (${tabCounts.completed})`}
          </Button>
          <Button
            variant="neutral"
            className={styles.customTabStyle}
            data-active={activeTab === "failed"}
            onClick={() => setActiveTab("failed")}
          >
            {`Failed (${tabCounts.failed})`}
          </Button>
          {canSeeMyReviewsTab(user?.roles) && (
            <Button
              variant="neutral"
              className={styles.customTabStyle}
              data-active={activeTab === "reviews"}
              onClick={() => setActiveTab("reviews")}
            >
              My Reviews (0)
            </Button>
          )}
        </div>

        {/* Sort Indicator */}
        <Flex justifyContent="flex-end" alignItems="center" className={styles.sortIndicator}>
          <Text variant="secondary-sm" className={styles.sortIndicatorText}>
            {filteredJobs.length} {filteredJobs.length === 1 ? "job" : "jobs"} found
          </Text>
        </Flex>

        {/* Enhanced Jobs List */}
        <div className={styles.jobsGridStyle}>
          {filteredJobs.map((job) => {
            const isHighlighted = ["AWAITING_APPROVAL"].includes(
              getEffectiveStatus(job.jobType, job.jobStatus),
            );

            return (
              <Card
                key={job.jobId}
                className={`inset-md ${isHighlighted ? styles.jobCardHighlighted : styles.jobCard}`}
              >
                {/* Header with title, status badges, and Details button */}
                <Flex
                  justifyContent="space-between"
                  alignItems="flex-start"
                  className={styles.jobCardHeader}
                >
                  <div className={styles.jobCardFlexContainer}>
                    <Flex alignItems="center" className={styles.jobCardTitleGap}>
                      <Text variant="strong-lg" className={styles.jobCardTitle}>
                        {job.customerName}
                      </Text>
                      <Tag
                        variant="neutral"
                        className={`${styles.statusTag} ${getStatusTagClassName(
                          getEffectiveStatus(job.jobType, job.jobStatus),
                        )}`}
                      >
                        {getStatusLabel(getEffectiveStatus(job.jobType, job.jobStatus))}
                      </Tag>
                    </Flex>
                  </div>
                  <Button
                    variant="neutral"
                    icon={
                      job.jobType === DataStudioJobTypeEnum.CrmImport &&
                      job.substate !== DataStudioJobSubstateEnum.ScheduleImport
                        ? "arrow-right"
                        : "eye"
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDetailsClick(job);
                    }}
                  >
                    {job.jobType === DataStudioJobTypeEnum.CrmImport &&
                    job.substate !== DataStudioJobSubstateEnum.ScheduleImport
                      ? "Go To Page"
                      : "Details"}
                  </Button>
                </Flex>

                {/* Job Metadata */}
                <div className={styles.jobMetadata}>
                  {job.jobType === DataStudioJobTypeEnum.CrmImport && job.jobName && (
                    <Flex alignItems="center" className={styles.jobMetadataRow}>
                      <Icon type="tag" size="small" color={Colors.Neutral500} />
                      <Text variant="secondary-sm" className={styles.jobMetadataText}>
                        Job Name: {job.jobName}
                      </Text>
                    </Flex>
                  )}
                  <Flex alignItems="center" className={styles.jobMetadataRow}>
                    <Icon type="user" size="small" color={Colors.Neutral500} />
                    <Text variant="secondary-sm" className={styles.jobMetadataText}>
                      PO User: {job.createdBy || ""}
                    </Text>
                  </Flex>
                  <Flex alignItems="center" className={styles.jobMetadataRow}>
                    <Icon type="globe" size="small" color={Colors.Neutral500} />
                    <Text variant="secondary-sm" className={styles.jobMetadataText}>
                      Environment:{" "}
                      <span className={styles.capitalizeText}>{job.environment}</span>
                    </Text>
                  </Flex>
                  <Flex alignItems="center" className={styles.jobMetadataRow}>
                    <Icon type="briefcase" size="small" color={Colors.Neutral500} />
                    <Text variant="secondary-sm" className={styles.jobMetadataText}>
                      Job Type: {formatJobType(job.jobType)}
                    </Text>
                  </Flex>
                </div>

                {/* Progress Section */}
                <div className={styles.jobProgress}>
                  <Flex
                    justifyContent="space-between"
                    alignItems="center"
                    className={styles.jobProgressHeader}
                  >
                    <Text variant="secondary-sm" className={styles.jobProgressText}>
                      Progress
                    </Text>
                    <Text variant="secondary-sm" className={styles.jobProgressPercentage}>
                      {getEffectiveStatus(job.jobType, job.jobStatus) ===
                      EffectiveStatus.COMPLETED
                        ? "100%"
                        : "0%"}
                    </Text>
                  </Flex>
                  <div className={styles.jobProgressBar}>
                    <div
                      className={`${styles.jobProgressFill} ${getProgressFillClassName(
                        getEffectiveStatus(job.jobType, job.jobStatus),
                      )}`}
                    />
                  </div>
                </div>

                {/* Timestamp Section */}
                <Flex justifyContent="space-between" alignItems="center">
                  <Flex alignItems="center" className={styles.timestampRow}>
                    <Icon type="calendar" size="small" color={Colors.Neutral500} />
                    <Text variant="secondary-sm" className={styles.jobMetadataText}>
                      Started:{" "}
                      {new Date(Number(job.createdAt) * 1000)
                        .toUTCString()
                        .replace("GMT", "UTC")}
                    </Text>
                  </Flex>
                </Flex>
              </Card>
            );
          })}
        </div>

        {filteredJobs.length === 0 && (
          <Card className="inset-lg">
            <Flex justifyContent="center" alignItems="center" flexDirection="column">
              <Text variant="strong-lg" className={styles.noJobsTitle}>
                No jobs found
              </Text>
              <Text variant="secondary-md" className={styles.noJobsText}>
                Try adjusting your search terms
              </Text>
            </Flex>
          </Card>
        )}

        {/* Jobs Count Info */}
        {jobs.length > 0 && (
          <Flex justifyContent="center" alignItems="center" className={styles.jobsCountInfo}>
            <Text variant="secondary-sm" className={styles.noJobsText}>
              Showing {filteredJobs.length} of{" "}
              {onboardingJobsData?.listOnboardingJobs?.totalCount || 0} total jobs
            </Text>
          </Flex>
        )}
      </Card>

      {/* Job Logs Modal */}
      {selectedJob && (
        <JobLogsModal
          job={selectedJob}
          isOpen={isJobLogsOpen}
          onClose={() => {
            setIsJobLogsOpen(false);
            setSelectedJob(null);
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;
